/**
 * Hook to detect fields that had values in the original order but are now removed/disabled.
 * These fields should still appear in Final Review to show what was removed.
 */

import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FIELD_LABELS} from './constants'
import {
  useFieldFormatters,
  type FormatterContext,
  type FinalReviewItemData
} from './fieldFormatters'

/**
 * All changeable field IDs that could potentially have values removed.
 * These are checked even when disabled in configuration to show removed values.
 */
const ALL_CHANGEABLE_FIELD_IDS = [
  'material',
  'quantity',
  'date',
  'time',
  'intervals',
  'siteContact',
  'poNumber',
  'driverInstructions',
  'additionalNotes',
  'usage',
  'materialTesting',
  'retarder'
]

interface UseRemovedFieldItemsProps {
  /** Set of field IDs that are currently enabled in configuration */
  enabledFieldIds: Set<string>
  /** Formatter context with translation function */
  context: FormatterContext
}

/**
 * Hook that checks for fields that were removed (had original values but are now disabled).
 *
 * @returns Map of field IDs to their FinalReviewItemData for removed fields only
 */
export const useRemovedFieldItems = ({
  enabledFieldIds,
  context
}: UseRemovedFieldItemsProps): Map<string, FinalReviewItemData> => {
  const {t} = useTranslation()
  const fieldFormatters = useFieldFormatters()

  return useMemo(() => {
    const removedItems = new Map<string, FinalReviewItemData>()

    ALL_CHANGEABLE_FIELD_IDS.forEach((fieldId) => {
      // Skip if field is enabled (already handled elsewhere)
      if (enabledFieldIds.has(fieldId)) {
        return
      }

      const formatter = fieldFormatters[fieldId]
      if (!formatter) {
        return
      }

      const {values, requiresApproval, hasChanges} = formatter(context)

      // Only add if there are changes (meaning original data was removed)
      if (hasChanges) {
        const labelConfig = FIELD_LABELS[fieldId]
        if (labelConfig) {
          removedItems.set(fieldId, {
            fieldId,
            label: t(labelConfig.key, labelConfig.fallback),
            values,
            requiresApproval,
            hasChanges
          })
        }
      }
    })

    return removedItems
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledFieldIds, context, t])
}
